import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Check, Loader2 } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { SEO } from '@/components/SEO';
import { Reveal } from '@/components/Reveal';
import { StarRating } from '@/components/marketplace/StarRating';
import { toast } from '@/components/ui/sonner';
import { getReviewEligibility, submitReview, apiErrorMessage } from '@/lib/api';
import { REVIEW_PAGE as T } from '@/constants/testIds';

const cardClass = 'rounded-2xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10] p-8 text-center';

export default function ReviewSubmit() {
  const { bookingId } = useParams();
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [state, setState] = useState({ status: 'loading' });
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getReviewEligibility(bookingId, token)
      .then((d) => { if (!cancelled) setState({ status: 'ready', ...d }); })
      .catch(() => { if (!cancelled) setState({ status: 'error' }); });
    return () => { cancelled = true; };
  }, [bookingId, token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!rating) { toast.error('Please select a star rating.'); return; }
    setSubmitting(true);
    try {
      await submitReview({ booking_id: bookingId, token, rating, comment: comment.trim() });
      setDone(true);
      toast.success('Thanks for your review!');
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not submit your review.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="pt-16 min-h-[70vh] bg-[#0A0A0A] px-4 py-20" data-testid={T.root}>
      <SEO title="Rate your session" description="Share your experience with your LXRA photographer." />
      <div className="mx-auto max-w-lg">
        {state.status === 'loading' && <p className="text-sm text-[hsl(var(--lxra-muted))] text-center">Loading…</p>}

        {state.status === 'error' && (
          <div data-testid={T.invalid} className={cardClass}>
            <h1 className="font-display text-2xl text-[hsl(var(--lxra-fg))]">LINK NOT FOUND</h1>
            <p className="mt-2 text-sm text-[hsl(var(--lxra-muted))]">This review link doesn’t look right. Please check the email link and try again.</p>
            <Link to="/" className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.1em] text-[hsl(var(--lxra-gold))] hover:text-[hsl(var(--lxra-gold-hover))]"><ArrowLeft className="h-3.5 w-3.5" /> Back to home</Link>
          </div>
        )}

        {state.status === 'ready' && !state.ok && (
          <div data-testid={T.invalid} className={cardClass}>
            <h1 className="font-display text-2xl text-[hsl(var(--lxra-fg))]">{state.reason === 'not_delivered' ? 'NOT READY YET' : 'LINK INVALID'}</h1>
            <p className="mt-2 text-sm text-[hsl(var(--lxra-muted))]">
              {state.reason === 'not_delivered' ? 'Reviews open once your session has been marked delivered.' : 'This review link is invalid or has expired.'}
            </p>
          </div>
        )}

        {state.status === 'ready' && state.ok && state.already_reviewed && !done && (
          <div data-testid={T.alreadyReviewed} className={`${cardClass} border-[hsl(var(--lxra-gold))]/30`}>
            <span className="flex h-12 w-12 mx-auto items-center justify-center rounded-full bg-[hsl(var(--lxra-gold))] text-[#0A0A0A]"><Check className="h-6 w-6" /></span>
            <h1 className="font-display text-2xl text-[hsl(var(--lxra-fg))] mt-5">ALREADY SUBMITTED</h1>
            <p className="mt-2 text-sm text-[hsl(var(--lxra-muted))]">You’ve already reviewed your session with {state.studio_name}. Thank you!</p>
          </div>
        )}

        {state.status === 'ready' && state.ok && !state.already_reviewed && !done && (
          <Reveal>
            <p className="text-xs tracking-[0.22em] uppercase text-[hsl(var(--lxra-gold))]">Rate your session</p>
            <h1 className="font-display text-3xl sm:text-4xl tracking-[0.03em] text-[hsl(var(--lxra-fg))] mt-2">HOW WAS {state.studio_name?.toUpperCase()}?</h1>
            <div className="mt-4 h-[2px] w-16 bg-[hsl(var(--lxra-gold))]" />
            <form onSubmit={handleSubmit} className="mt-8 space-y-6">
              <div>
                <p className="text-xs uppercase tracking-[0.1em] text-[hsl(var(--lxra-muted))] mb-2">Your rating</p>
                <StarRating value={rating} interactive onChange={setRating} size="h-8 w-8" testId={T.starInput} />
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.1em] text-[hsl(var(--lxra-muted))] mb-2">Your review (optional)</p>
                <Textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  rows={5}
                  maxLength={1000}
                  placeholder="Tell other clients about your experience…"
                  data-testid={T.commentInput}
                  className="bg-[#141416] border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))] focus-visible:ring-[hsl(var(--lxra-gold))]"
                />
              </div>
              <Button type="submit" disabled={submitting} data-testid={T.submitButton} className="h-12 px-8 rounded-xl bg-[hsl(var(--lxra-gold))] text-[#0A0A0A] hover:bg-[hsl(var(--lxra-gold-hover))] text-xs uppercase tracking-[0.1em] font-semibold disabled:opacity-60">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null} Submit review
              </Button>
            </form>
          </Reveal>
        )}

        {done && (
          <div data-testid={T.success} className={`${cardClass} border-[hsl(var(--lxra-gold))]/40`}>
            <span className="flex h-12 w-12 mx-auto items-center justify-center rounded-full bg-[hsl(var(--lxra-gold))] text-[#0A0A0A]"><Check className="h-6 w-6" /></span>
            <h1 className="font-display text-2xl text-[hsl(var(--lxra-fg))] mt-5">THANK YOU.</h1>
            <p className="mt-2 text-sm text-[hsl(var(--lxra-muted))]">Your review helps other clients find great photographers.</p>
          </div>
        )}
      </div>
    </div>
  );
}
